import React from 'react';
import {NativeFetchError} from '../../utils/errors';
import type {ApiFetchFn} from '../../utils/fetch';
import {FetchApiError} from '../../utils/fetch';

const FetchContext = React.createContext<ApiFetchFn | null>(null);
FetchContext.displayName = 'FetchContext';

// eslint-disable-next-line @stripe-internal/stripe/no-mixed-react-exports
export const isNativeFetchError = (
  error: unknown,
): error is NativeFetchError => {
  return error instanceof NativeFetchError;
};

// eslint-disable-next-line @stripe-internal/stripe/no-mixed-react-exports
export const handleFetchResponse = async (
  fetchResult: Response,
): Promise<any> => {
  try {
    const jsonResponse = await fetchResult.json();

    if (!fetchResult.ok) {
      const error = jsonResponse.error || {};
      const errorMsg = error?.message || 'unknown error';
      throw new FetchApiError(
        `HTTP status code: ${fetchResult.status}. Reason: ${errorMsg}`,
        {
          code: error?.code,
          httpStatus: fetchResult.status,
          message: error?.message,
          messageCode: error?.message_code,
          requestId: fetchResult.headers?.get('request-id') || undefined,
          type: error?.type,
        },
      );
    }

    return jsonResponse;
  } catch (error) {
    // Passthrough this kind of error
    if (error && error instanceof FetchApiError) {
      throw error;
    }

    // All other errors are converted to native fetch errors
    if (error && error instanceof Error) {
      throw new NativeFetchError(error?.message, error);
    }
    throw new NativeFetchError('Unknown error', error);
  }
};

export const FetchContextProvider: React.FC<{
  fetchOverride: typeof fetch;
}> = ({fetchOverride, children}) => {
  const wrappedFetchOverride: ApiFetchFn = async (...params) => {
    const fetchResult = await fetchOverride(...params);
    return handleFetchResponse(fetchResult);
  };

  return (
    <FetchContext.Provider value={wrappedFetchOverride}>
      {children}
    </FetchContext.Provider>
  );
};

// eslint-disable-next-line @stripe-internal/stripe/no-mixed-react-exports
export const useApiFetch = () => {
  return React.useContext(FetchContext)!;
};
